import { toast } from "react-toastify";
import { deleteItems, updateStateData } from "../Store/actions";
import store from "../Store/store";
import { sendApiRequest } from "./requestConfig";
import { validateAlphanumeric } from "./utils";
import { checkUserDetailsFromCookie } from "./userManagement";

/** Tenant/root company for the current login (impressions "(tenant)" suffix). */
export const getTenantCompanyId = () => {
    const userDetail = checkUserDetailsFromCookie()?.user_detail;

    if (typeof userDetail?.root_company_id !== "undefined" && userDetail.root_company_id !== null) {
        return userDetail.root_company_id;
    }
    if (typeof userDetail?.current_workspace?.id !== "undefined") {
        return userDetail.current_workspace.id;
    }
    if (typeof userDetail?.assigned_company?.id !== "undefined") {
        return userDetail.assigned_company.id;
    }

    return null;
};

/** Impressions list/dialog: tenant company is shown as "Name (tenant)". */
export const formatImpressionCompanyLabel = (companyName, companyId, tenantCompanyId) => {
    const name = typeof companyName === "string" && companyName !== "" ? companyName : "-";

    if (name === "-") {
        return "-";
    }

    if (
        typeof tenantCompanyId !== "undefined" &&
        tenantCompanyId !== null &&
        typeof companyId !== "undefined" &&
        companyId !== null &&
        String(companyId) === String(tenantCompanyId)
    ) {
        return `${name} (tenant)`;
    }

    return name;
};

// to check the available report sections wrt workspace and also the selected settings for a particular campaign
export const fetchReportFilters = async () => {
    // call api
    const fetchFilters = await sendApiRequest("reports/filters", "", "GET");

    //   evaluate api response
    if (typeof fetchFilters.success !== "undefined" && fetchFilters.success === true) {

        store.dispatch(
            updateStateData({
                workspace_report_filters: fetchFilters.response,
            })
        );
        return {
            success: true,
        };
    } else {
        return {
            success: false,
        };
    }
};

export const validateNewWorkspaceSection = (sectionType) => {
    const userStateData = store.getState().userSelections;
    let error = false;
    let errorDetails = {};
    // section id (If editing)
    const sectionId = typeof userStateData.workspace_section_selections !== "undefined" && typeof userStateData.workspace_section_selections.section_id !== "undefined" ? userStateData.workspace_section_selections.section_id : "";
    // section name
    const sectionName = typeof userStateData.workspace_section_selections !== "undefined" && typeof userStateData.workspace_section_selections.section_name !== "undefined" ? userStateData.workspace_section_selections.section_name : "";
    // embeddable section id
    const embeddableId = typeof userStateData.workspace_section_selections !== "undefined" && typeof userStateData.workspace_section_selections.embeddable_section_id !== "undefined" ? userStateData.workspace_section_selections.embeddable_section_id : "";
    // section description
    const sectionDesc = typeof userStateData.workspace_section_selections !== "undefined" && typeof userStateData.workspace_section_selections.section_description !== "undefined" ? userStateData.workspace_section_selections.section_description : "";
    // company_id is set for a company-specific impression section, on add and on update.
    const sectionCompanyId = typeof userStateData.workspace_section_selections !== "undefined" && typeof userStateData.workspace_section_selections.company_id !== "undefined" ? userStateData.workspace_section_selections.company_id : "";

    const filterSelection = typeof userStateData.workspace_filter_selections !== "undefined" ? userStateData.workspace_filter_selections : [];

    // validation checks
    const validateSectionName = validateAlphanumeric(sectionName, false);
    const validateSectionDesc = { error: false };

    const validateEmbeddableId = validateAlphanumeric(embeddableId, true);

    if (validateSectionName.error === true) {
        error = true;
        errorDetails = {
            ...errorDetails,
            section_name: "Section Name must not be empty and may not contain special characters",
        };
    }

    if (validateEmbeddableId.error === true) {
        error = true;
        errorDetails = {
            ...errorDetails,
            embeddable_section_id: "Embeddable ID must not be empty and may only contain letter, number, and hyphens",
        };
    }

    if (validateSectionDesc.error === true) {
        error = true;
        errorDetails = {
            ...errorDetails,
            section_description: "", //Error should be posible anymore because the field is optional and Character constrainst has been removed
        };
    }

    if (error === true) {
        return {
            error: true,
            error_messages: { ...errorDetails },
        };
    } else {
        let requestData = {
            "name": sectionName,
            "id_embeddable": embeddableId,
            "id_filters": filterSelection,
            "type": sectionType,
            "description": sectionDesc
        }

        if (sectionId !== "") {
            requestData = {
                ...requestData,
                "id": sectionId
            }
        }

        if (sectionCompanyId !== "") {
            requestData = {
                ...requestData,
                "id_company": sectionCompanyId
            }
        }

        return {
            error: false,
            request_data: requestData
        }
    }
};

export const resetNewSectionDetails = () => {
    store.dispatch(deleteItems(["workspace_section_selections", "workspace_filter_selections"]));
};

/**
 * Turn impressions API rows into SectionsList-ready objects.
 * - section present → configured row (edit)
 * - section null → placeholder row (__missingImpressionSection: true, add)
 */
const normalizeImpressionSectionRows = (apiList) => {
    const list = Array.isArray(apiList) ? apiList : [];

    return list.map((entry) => {
        const companyId = entry?.id;
        const companyName = entry?.name ?? "";

        if (entry?.section) {
            return {
                ...entry.section,
                company_name: entry.section.company_name ?? companyName,
                __missingImpressionSection: false,
                __retailerCompanyId: companyId,
                __retailerCompanyName: companyName,
            };
        }

        // No section yet for this company — keep company id as row id
        return {
            id: companyId,
            company_name: companyName,
            __missingImpressionSection: true,
            __retailerCompanyId: companyId,
            __retailerCompanyName: companyName,
            type: "impression",
            archived: false,
        };
    });
};

// Performance sections (available + archived come from the same list; UI filters by archived)
export const fetchPerformanceSections = async () => {
    const fetchSections = await sendApiRequest("reports/sections/performances", "", "GET");

    if (typeof fetchSections.success !== "undefined" && fetchSections.success === true) {
        store.dispatch(
            updateStateData({
                workspace_performance_sections: fetchSections.response,
            })
        );
        return { success: true };
    }

    return { success: false };
};

// Impressions sections — one row per company from the API (configured or missing)
export const fetchImpressionSections = async () => {
    const fetchSections = await sendApiRequest("reports/sections/impressions", "", "GET");

    if (typeof fetchSections.success !== "undefined" && fetchSections.success === true) {
        store.dispatch(
            updateStateData({
                workspace_impression_sections: normalizeImpressionSectionRows(fetchSections.response),
            })
        );
        return { success: true };
    }

    return { success: false };
};

// Load both section lists (popup mount + after add/edit/archive/delete)
export const fetchWorkspaceSections = async () => {
    const [performanceResult, impressionResult] = await Promise.all([
        fetchPerformanceSections(),
        fetchImpressionSections(),
    ]);

    return {
        success: performanceResult.success === true && impressionResult.success === true,
    };
};

// to save new section for the reports
export const saveNewSectionForWorkspace = async (sectionType, usedFor) => {
    // validate data
    const validateData = validateNewWorkspaceSection(sectionType);
    if (validateData.error === true) {
        toast("Please check the errors.", { type: "error" });
        return validateData;
    } else {
        // call api
        const apiEndpoint = usedFor === "add" ? "reports/sections/add" : "reports/sections/update";
        const saveSection = await sendApiRequest(apiEndpoint, validateData.request_data, "POST");
        //   evaluate api response
        if (typeof saveSection.success !== "undefined" && saveSection.success === true) {
            resetNewSectionDetails();
            await fetchWorkspaceSections();
            toast("New section saved successfully", { type: "success" });
            return {
                success: true,
            };
        } else {
            toast("Sorry, something went wrong.", { type: "error" });
            return {
                success: false,
            };
        }
    }

};

// archive section
export const archiveWorkspaceSection = async (sectionId) => {
    // call api
    const archiveSections = await sendApiRequest("reports/sections/archive/" + sectionId,
        {
            "archived": true
        },
        "POST");
    //   evaluate api response
    if (typeof archiveSections.success !== "undefined" && archiveSections.success === true) {
        await fetchWorkspaceSections();
        toast("Section archived successfully", { type: "success" });
        return {
            success: true,
        };
    }
    else {
        return {
            success: false,
        };
    }
}

export const recoverWorkspaceSection = async (sectionId) => {
    // call api
    const recoverSections = await sendApiRequest("reports/sections/archive/" + sectionId,
        {
            "archived": false
        },
        "POST");
    //   evaluate api response
    if (typeof recoverSections.success !== "undefined" && recoverSections.success === true) {
        await fetchWorkspaceSections();
        toast("Section recovered successfully", { type: "success" });
        return {
            success: true,
        };
    }
    else {
        return {
            success: false,
        };
    }
}

export const deleteWorkspaceSection = async (sectionId) => {
    // call api
    const deleteSections = await sendApiRequest(
        "reports/sections/delete/" + sectionId,
        {},
        "POST"
    );
    //   evaluate api response
    if (typeof deleteSections.success !== "undefined" && deleteSections.success === true) {
        await fetchWorkspaceSections();
        toast("Section deleted successfully", { type: "success" });
        return {
            success: true,
        };
    }
    else {
        return {
            success: false,
        };
    }
}

export const checkSectionAvailability = async () => {
    // call api
    const checkSections = await sendApiRequest(
        "reports/sections/availability",
        {},
        "GET"
    );
    //   evaluate api response
    if (typeof checkSections.success !== "undefined" && checkSections.success === true) {
        const apiResponse = checkSections.response || {};

        // Normalize for easy lookups in the campaign list:
        // - impression_sections: { [rootRetailerId]: true/false }
        // - performance_section: tenant-wide boolean
        const impressionSectionsMap = {};
        const impressionList = Array.isArray(apiResponse.impression_section)
            ? apiResponse.impression_section
            : [];

        impressionList.forEach((entry) => {
            if (typeof entry?.id !== "undefined" && entry?.id !== null) {
                impressionSectionsMap[entry.id] = entry.available === true;
            }
        });

        store.dispatch(
            updateStateData({
                report_section_availability: {
                    impression_sections: impressionSectionsMap,
                    performance_section: apiResponse.performance_section,
                },
            })
        );
        return {
            success: true,
        };
    }
    else {
        return {
            success: false,
        };
    }
}